import { useEffect, useRef, useState } from "react";
import { Box, Button, Divider, Flex, Text } from "@chakra-ui/react";

/**
 * 是否為「測試環境」:
 *  - 本機開發(npm start,NODE_ENV=development)
 *  - 部署測試站:build 時測試/正式都是 production,差異在 .env
 *    測試站 REACT_APP_WEB_NAME 帶「_T」、API/WS 走 snd./test 網域;正式站不含這些字樣。
 *  若日後測試站命名規則改變,調整這裡即可。
 */
export const isTestEnv =
  process.env.NODE_ENV === "development" ||
  /snd|test|_t/i.test(
    `${process.env.REACT_APP_API_URL || ""} ${
      process.env.REACT_APP_WS_HOST || ""
    } ${process.env.REACT_APP_WEB_NAME || ""}`
  );

const GOLD = "#d3b552";
const TEST_REQ_ID = "TEST_REQ"; // transfer_in_game 測試用固定單號
const AUTO_SUCCESS_DELAY_MS = 5000; // 「pending → N 秒後完成」測試鈕的延遲

/**
 * DevTestPanel — 統一測試面板(僅測試環境顯示,正式站自動回傳 null)。
 *
 * 透過 onProcess 呼叫「真實」的 processSocketMessage,模擬各種 WS 訊息,
 * 測到的即實際邏輯、不會 drift。deposit 走 onSimDeposit(僅動畫,不觸發 all-in API)。
 *
 * @param {number|string} uid 會員 uid
 * @param {number|string|null} currentGameUid 當前遊戲 uid
 * @param {(parsed:{type:string, member_info_uid?:any, data?:any, memo?:any})=>void} onProcess
 * @param {(amount?:number)=>void} onSimDeposit 上分動畫(僅動畫)
 */
const DevTestPanel = ({ uid, currentGameUid, onProcess, onSimDeposit }) => {
  const [open, setOpen] = useState(false);
  // 「pending → 自動完成」倒數中,避免重複按而疊出多個計時器
  const [autoRunning, setAutoRunning] = useState(false);
  const autoTimerRef = useRef(null);

  // hooks 必須在 isTestEnv 早退之前宣告完
  useEffect(() => () => clearTimeout(autoTimerRef.current), []);

  if (!isTestEnv) return null;

  const inGameBox =
    typeof window !== "undefined" &&
    window.location.pathname.includes("gamebox");

  // 各類模擬
  const simMaintainMember = (mode) => {
    let data;
    if (mode === "matchGameId") {
      data = { msg: "【測試】當前遊戲維護(game_id 命中)", game_id: [currentGameUid] };
    } else if (mode === "otherGameId") {
      data = { msg: "【測試】其他遊戲維護(game_id 不含當前)", game_id: ["__NOT_MATCH__"] };
    } else {
      data = "【測試】會員層級維護(無 game_id)";
    }
    console.log("[test] maintain_notice_member", { mode, currentGameUid, inGameBox, data });
    onProcess?.({ type: "maintain_notice_member", member_info_uid: uid, data });
  };

  const simFullMaintain = () => {
    const data = "【測試】全站維護時間";
    console.log("[test] maintain_notice", data);
    onProcess?.({ type: "maintain_notice", data });
  };

  const simTransCode = (ok) => {
    const parsed = ok
      ? { type: "transCode", member_info_uid: uid, data: "ok" }
      : { type: "transCode", member_info_uid: uid, data: "fails", memo: "【測試】轉點失敗訊息" };
    console.log("[test] transCode", parsed);
    onProcess?.(parsed);
  };

  const simTransferInGame = (status) => {
    const data =
      status === "pending"
        ? { request_id: TEST_REQ_ID, status: "pending" }
        : { request_id: TEST_REQ_ID, status, amount: 888, platform: "TEST" };
    console.log("[test] transfer_in_game", data);
    onProcess?.({ type: "transfer_in_game", member_info_uid: uid, data });
  };

  /**
   * 轉點的完整流程:pending → (等後端) → success。
   * 後端目前不一定會主動推送完成,手動按兩顆很難重現「等待後結果才進來」的時序,
   * 這顆一次跑完:先送 pending,AUTO_SUCCESS_DELAY_MS 後自動送 success。
   */
  const simTransferInGameAuto = () => {
    if (autoRunning) return;
    setAutoRunning(true);
    simTransferInGame("pending");
    autoTimerRef.current = setTimeout(() => {
      simTransferInGame("success");
      setAutoRunning(false);
    }, AUTO_SUCCESS_DELAY_MS);
  };

  const simSocketError = () => {
    const data = "【測試】Socket 錯誤訊息";
    console.log("[test] error", data);
    onProcess?.({ type: "error", member_info_uid: uid, data });
  };

  const sectionTitle = (t) => (
    <Text fontSize="10px" color={GOLD} fontWeight="700" mt={1}>
      {t}
    </Text>
  );
  const btn = (label, onClick, scheme = "gray") => (
    <Button size="xs" colorScheme={scheme} onClick={onClick} justifyContent="flex-start">
      {label}
    </Button>
  );

  return (
    <Box position="fixed" left="8px" bottom="8px" zIndex={9999999}>
      {/* 收合開關 */}
      <Button
        size="xs"
        bg="rgba(0,0,0,0.8)"
        color={GOLD}
        border="1px solid rgba(211,181,82,0.6)"
        _hover={{ bg: "rgba(0,0,0,0.9)" }}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "▼ 測試面板" : "▶ 測試面板"}
      </Button>

      {open && (
        <Flex
          direction="column"
          gap="5px"
          mt="6px"
          p="8px"
          w="230px"
          maxH="70vh"
          overflowY="auto"
          bg="rgba(0,0,0,0.85)"
          borderRadius="8px"
          border="1px solid rgba(211,181,82,0.5)"
        >
          <Text fontSize="10px" color="whiteAlpha.700">
            當前遊戲:{String(currentGameUid ?? "無")} / gamebox:{inGameBox ? "是" : "否"}
          </Text>

          {sectionTitle("上分動畫(deposit)")}
          {btn("上分動畫 +321(僅動畫)", () => onSimDeposit?.(321), "green")}

          {sectionTitle("game_maintain 維護")}
          {btn("game_id 命中當前(需在 gamebox)", () => simMaintainMember("matchGameId"), "orange")}
          {btn("game_id 不含當前(應靜默)", () => simMaintainMember("otherGameId"))}
          {btn("無 game_id(會員層級,50s reload)", () => simMaintainMember("noGameId"), "purple")}

          {sectionTitle("全站維護 maintain_notice")}
          {btn("全站維護公告", simFullMaintain, "red")}

          {sectionTitle("轉點 / 轉入 / 錯誤")}
          {btn("轉點失敗(顯示訊息)", () => simTransCode(false))}
          {btn("轉點成功(棄分動畫)", () => simTransCode(true), "green")}
          {btn("轉入處理中 pending", () => simTransferInGame("pending"), "yellow")}
          {btn("轉入完成 success", () => simTransferInGame("success"), "green")}
          {btn(
            autoRunning
              ? `完成倒數中…(${AUTO_SUCCESS_DELAY_MS / 1000}s)`
              : `pending → ${AUTO_SUCCESS_DELAY_MS / 1000} 秒後完成`,
            simTransferInGameAuto,
            "teal"
          )}
          {btn("Socket 錯誤訊息", simSocketError, "red")}

          <Divider borderColor="whiteAlpha.200" my={1} />
          <Text fontSize="9px" color="whiteAlpha.500">
            僅測試環境顯示;正式站自動隱藏
          </Text>
        </Flex>
      )}
    </Box>
  );
};

export default DevTestPanel;
